import type { AppAction } from '../actions';
import { DEFAULT_WIDGET_ACTIVE_BORDER_COLOR } from '../defaults/workspace';
import type { WorkspaceViewModel } from '../types';
import { isEqual } from 'es-toolkit';

export interface WorkspaceState {
    currentWorkspace: WorkspaceViewModel | null;
    currentWorkspaceStatus: 'initial' | 'pending' | 'saving' | 'error' | 'success';
    currentWorkspaceError: 'permission' | 'generic' | null;
}

export const initialState: WorkspaceState = {
    currentWorkspace: null,
    currentWorkspaceStatus: 'initial',
    currentWorkspaceError: null,
};

export const UNSAVED_WORKSPACE: WorkspaceViewModel = {
    id: 'new',
    name: 'Untitled Workspace',
    isUnsaved: true,
    isDirty: false,
    isTemplate: false,
    isDefaultWorkspace: false,
    containerIds: [],
    activeContainerId: undefined,
    maximizedContainerId: undefined,
    permissions: {
        effectiveAccess: 'owner',
        effectiveDirectAccess: 'owner',
        canAdmin: true,
        canRead: true,
        canWrite: true,
        doesOwn: true,
        isShared: false,
        isDirectAccessComplex: false,
    },
    layoutSettings: {
        containerLayoutLocked: false,
        gridSnappingEnabled: true,
        enableMosaicLayout: false,
        mosaicGutterPx: 0,
    },
    description: '',
    activeBorderColor: DEFAULT_WIDGET_ACTIVE_BORDER_COLOR,
};

export const workspace = (state = initialState, action: AppAction): WorkspaceState => {
    switch (action.type) {
        case 'setResearchingWorkspace': {
            return {
                ...state,
                currentWorkspaceStatus: 'pending',
                currentWorkspaceError: null,
            };
        }

        case 'completeWorkspaceResearch': {
            return {
                ...state,
                currentWorkspaceStatus: 'success',
            };
        }

        case 'clearWorkspaceState': {
            return { ...initialState };
        }

        case 'initializeUnsavedWorkspace': {
            return {
                ...state,
                currentWorkspace: {
                    ...UNSAVED_WORKSPACE,
                    ...(action.name !== undefined ? { name: action.name } : {}),
                    ...(action.newWorkspace !== undefined ? { newWorkspace: action.newWorkspace } : {}),
                    layoutSettings: {
                        ...UNSAVED_WORKSPACE.layoutSettings,
                        enableMosaicLayout: action.enableMosaicLayout,
                        ...(action.gridSnappingEnabled !== undefined
                            ? { gridSnappingEnabled: action.gridSnappingEnabled }
                            : {}),
                        ...(action.mosaicGutterPx !== undefined ? { mosaicGutterPx: action.mosaicGutterPx } : {}),
                    },
                    mosaicTemplate: action.mosaicTemplate,
                },
                currentWorkspaceStatus: 'success',
                currentWorkspaceError: null,
            };
        }

        case 'setWorkspaceError': {
            return {
                ...state,
                currentWorkspaceStatus: 'error',
                currentWorkspaceError: action.errorType,
            };
        }

        case 'initializeWorkspace': {
            return {
                ...state,
                currentWorkspace: action.workspace,
            };
        }

        case 'addContainer': {
            const currentWorkspace = state.currentWorkspace!;

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    isDirty: true,
                    containerIds: [...(currentWorkspace.containerIds ?? []), action.container.id],
                },
            };
        }

        case 'removeContainer': {
            const currentWorkspace = state.currentWorkspace!;
            const wasMaximizedContainer = action.containerId === currentWorkspace.maximizedContainerId;

            // Unmark template slot if the removed container was placed in one
            let mosaicTemplate = currentWorkspace.mosaicTemplate;
            if (mosaicTemplate) {
                const slotEntry = Object.entries(mosaicTemplate.slotContainerMap).find(
                    ([, cId]) => cId === action.containerId,
                );
                if (slotEntry) {
                    const [slotId] = slotEntry;
                    const { [slotId]: _, ...restMap } = mosaicTemplate.slotContainerMap;
                    mosaicTemplate = {
                        ...mosaicTemplate,
                        occupiedSlotIds: mosaicTemplate.occupiedSlotIds.filter((id) => id !== slotId),
                        slotContainerMap: restMap,
                    };
                }
            }

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    isDirty: true,
                    containerIds: (currentWorkspace.containerIds ?? []).filter((x) => x !== action.containerId),
                    ...(wasMaximizedContainer ? { maximizedContainerId: undefined } : {}),
                    mosaicTemplate,
                },
            };
        }

        /** just set the workspace to dirty */
        case 'updateWidget':
        case 'moveTab':
        case 'removeTagFromWatchList':
        case 'addTagToWatchList':
        case 'updateActiveTab':
        case 'removeTab':
        case 'addTab':
        case 'updateTabName':
        case 'setSelectedUniverse':
        case 'onTabChannelChange':
        case 'updateWorkspaceLayout':
        case 'updateMultipleWorkspaceLayouts':
        case 'setAlphaLensChartCollapse':
        case 'setSearchAlphaStrategy':
        case 'setSearchAlphaUniverse':
        case 'setSearchAlphaDisplayingIdeas':
        case 'setSearchAlphaDisplayingResults':
        case 'setSearchAlphaDisplayingSearch':
        case 'storeSearchAlphaSplitPercentage':
        case 'setScreenerIdea':
        case 'setScreenerDisplayingBuilder':
        case 'setScreenerDisplayingResults':
        case 'storeScreenerSplitPercentage':
        case 'setScannerIdea':
        case 'setScannerDisplayingResults':
        case 'setScannerDisplayingBuilder':
        case 'storeScannerSplitPercentage':
        case 'storeScannerUniverseId':
        case 'linkingUpdateTradeProInstrument':
        case 'setLocatesV2AccountIds':
        case 'setLocatesV2AutoAcceptType':
        case 'setLocatesV2AutoAcceptPercent':
        case 'setLocatesV2DisplayingQuote':
        case 'setLocatesV2DisplayingResults':
        case 'setLocatesV2DisplayingCostInfo':
        case 'storeLocatesV2SplitPercentage':
        case 'updateLocatesV2FormData':
        case 'updateLocatesV2QuickSendButtons':
        case 'setLocatesV2SoundAlert':
        case 'setLocatesV2QuantityKilo':
        case 'setLocatesV2UserOnly':
        case 'updateLocatesV2CostInfoColumnTemplate':
        case 'updateLocatesV2Filter':
        case 'updateLocatesV2ColumnWidths':
        case 'updateLocatesV2ColumnOrder':
        case 'updateLocatesV2ColumnVisibility':
        case 'setLocatesV2OnlyPending':
        case 'setLocatesV2OnlyResults':
        case 'updateLocatesV2Sort':
        case 'setWatchlistIdea':
        case 'updateActiveAlertsTab': {
            if ('skipDirtyCheck' in action && action.skipDirtyCheck === true) return state;
            const currentWorkspace = state.currentWorkspace;

            if (!currentWorkspace || (currentWorkspace.isDirty && state.currentWorkspaceStatus !== 'saving')) {
                return state;
            }

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    isDirty: true,
                },
            };
        }

        case 'setSavingTrueAction': {
            return {
                ...state,
                currentWorkspaceStatus: 'saving',
            };
        }

        case 'savedWorkspaceLoaded': {
            const current = state.currentWorkspace;
            if (!current || current.id !== action.submitted.id) return state;
            const { saved, submitted } = action;
            // Saved, it is no longer the unsaved workspace its spec created.
            const { newWorkspace: _createdFrom, ...persisted } = current;
            return {
                ...state,
                currentWorkspaceStatus: 'success',
                currentWorkspace: {
                    ...persisted,
                    id: saved.id,
                    permissions: saved.permissions,
                    isTemplate: saved.isTemplate,
                    isDefaultWorkspace: action.isDefaultWorkspace,
                    isDirty: action.hasUnsavedChanges,
                    isUnsaved: false,
                    name: current.name === submitted.name ? saved.name : current.name,
                    description:
                        current.description === submitted.description ? saved.description : current.description,
                    layoutSettings: isEqual(current.layoutSettings, submitted.layoutSettings)
                        ? saved.layoutSettings
                        : current.layoutSettings,
                    activeBorderColor:
                        current.activeBorderColor === submitted.activeBorderColor
                            ? saved.activeBorderColor
                            : current.activeBorderColor,
                },
            };
        }

        case 'workspaceSavingFailed': {
            return {
                ...state,
                currentWorkspaceStatus: 'error',
            };
        }

        case 'markWorkspaceDirty': {
            const currentWorkspace = state.currentWorkspace;
            // Preserve repeated edit notifications during a save. The service holds
            // the submitted workspace reference to detect edits before acknowledging it.
            if (!currentWorkspace || (currentWorkspace.isDirty && state.currentWorkspaceStatus !== 'saving')) {
                return state;
            }

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    isDirty: true,
                },
            };
        }

        case 'savingCancelledAction': {
            return {
                ...state,
                currentWorkspaceStatus: 'success',
            };
        }

        case 'updateWorkspaceSettings': {
            const currentWorkspace = state.currentWorkspace;

            if (!currentWorkspace) return state;

            const currentlyIsPrimary = currentWorkspace.isDefaultWorkspace;

            const {
                name,
                isPrimaryWorkspace,
                isLocked,
                snappingGridEnabled,
                enableMosaicLayout,
                activeBorderColor,
                mosaicGutterPx,
            } = action.settings;

            const anyChanges =
                currentWorkspace.name !== name ||
                currentlyIsPrimary !== isPrimaryWorkspace ||
                currentWorkspace.layoutSettings?.containerLayoutLocked !== isLocked ||
                currentWorkspace.layoutSettings?.gridSnappingEnabled !== snappingGridEnabled ||
                currentWorkspace.layoutSettings?.enableMosaicLayout !== enableMosaicLayout ||
                currentWorkspace.layoutSettings?.mosaicGutterPx !== mosaicGutterPx ||
                currentWorkspace.activeBorderColor !== activeBorderColor;

            if (!anyChanges) {
                return state;
            }

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    name: action.settings.name,
                    isDirty: false,
                    layoutSettings: {
                        ...currentWorkspace.layoutSettings,
                        containerLayoutLocked: isLocked,
                        gridSnappingEnabled: snappingGridEnabled,
                        enableMosaicLayout: enableMosaicLayout,
                        mosaicGutterPx: mosaicGutterPx,
                    },
                    activeBorderColor: action.settings.activeBorderColor,
                },
            };
        }
        case 'setMaximizedContainer': {
            const currentWorkspace = state.currentWorkspace!;
            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    maximizedContainerId: action.containerId,
                },
            };
        }

        case 'setMosaicTemplate': {
            const currentWorkspace = state.currentWorkspace;
            if (!currentWorkspace) return state;

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    mosaicTemplate: action.template,
                },
            };
        }

        case 'markMosaicTemplateSlotOccupied': {
            const currentWorkspace = state.currentWorkspace;
            if (!currentWorkspace?.mosaicTemplate) return state;

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    mosaicTemplate: {
                        ...currentWorkspace.mosaicTemplate,
                        occupiedSlotIds: [...currentWorkspace.mosaicTemplate.occupiedSlotIds, action.slotId],
                        slotContainerMap: {
                            ...currentWorkspace.mosaicTemplate.slotContainerMap,
                            [action.slotId]: action.containerId,
                        },
                    },
                },
            };
        }

        case 'setLayoutModeLocal': {
            const currentWorkspace = state.currentWorkspace;
            if (!currentWorkspace) return state;

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    isDirty: true,
                    layoutSettings: {
                        ...currentWorkspace.layoutSettings,
                        enableMosaicLayout: action.enableMosaicLayout,
                    },
                },
            };
        }

        case 'unmarkMosaicTemplateSlotOccupied': {
            const currentWorkspace = state.currentWorkspace;
            if (!currentWorkspace?.mosaicTemplate) return state;

            const { [action.slotId]: _, ...restMap } = currentWorkspace.mosaicTemplate.slotContainerMap;
            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    mosaicTemplate: {
                        ...currentWorkspace.mosaicTemplate,
                        occupiedSlotIds: currentWorkspace.mosaicTemplate.occupiedSlotIds.filter(
                            (id) => id !== action.slotId,
                        ),
                        slotContainerMap: restMap,
                    },
                },
            };
        }

        default:
            return state;
    }
};
